import { Link } from "@tanstack/react-router";
import {
  Bot,
  Boxes,
  type LucideIcon,
  MessageSquare,
  Receipt,
  Store,
  Trash2,
  Warehouse,
} from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { ActivityEvent } from "../api/use-activity";
import { categoryFor } from "../lib/activity-kind";
import { auditLogNavigationFor, auditNavigationForEvent } from "../lib/audit-link";

// ponytail: one icon per activity_kind category code (categoryFor). Monochrome
// by design — intent color was dropped, see status-taxonomy.json's activity_kind
// note. Add a case here if categoryFor ever returns a new code.
const CATEGORY_ICON: Record<string, LucideIcon> = {
  marketplace: Store,
  warehouse: Warehouse,
  stock: Boxes,
  note: MessageSquare,
  settlement: Receipt,
  atlas: Bot,
};

const BANGKOK_TZ = "Asia/Bangkok";
const timeFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: BANGKOK_TZ,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

interface ActivityRowProps {
  item: ActivityEvent;
  orderId: string;
  canDelete: boolean;
  onDelete: () => void;
}

export function ActivityRow({ item, orderId, canDelete, onDelete }: ActivityRowProps): JSX.Element {
  const { t } = useTranslation("orders");
  // `meta.sku` is the only field a stock_audit_id row carries the SKU under
  // (ActivityEvent has no dedicated sku column) — see audit-link.ts.
  const sku = typeof item.meta?.sku === "string" ? item.meta.sku : "";
  const category = categoryFor(item);
  // The feed folds identical warehouse retries into one row (BE
  // collapseRepeats) and says how many it stands for in meta.
  const occurrences = typeof item.meta?.occurrences === "number" ? item.meta.occurrences : 1;
  const firstSeen =
    typeof item.meta?.first_seen === "string"
      ? timeFormatter.format(new Date(item.meta.first_seen))
      : "";
  const CategoryIcon = CATEGORY_ICON[category] ?? MessageSquare;

  return (
    <div
      data-testid="activity-row"
      className="flex items-start gap-3 py-2.5 text-sm"
      data-source={item.source}
    >
      <span className="w-12 shrink-0 font-mono text-xs text-[var(--text-subtle)]">
        {timeFormatter.format(new Date(item.at))}
      </span>
      <span className="flex w-24 shrink-0 items-center gap-1.5 text-xs text-[var(--text-subtle)]">
        <CategoryIcon
          data-testid="activity-category-icon"
          aria-hidden
          className="h-3.5 w-3.5 shrink-0 text-[var(--text-subtle)]"
        />
        <span data-testid="activity-category" className="truncate">
          {t(`activity.category.${category}`)}
        </span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-[var(--text-primary)]">{item.title}</span>
        {occurrences > 1 && (
          <span
            data-testid="activity-occurrences"
            title={t("activity.repeated_title", { count: occurrences, since: firstSeen })}
            className="ml-1.5 rounded-full border border-[var(--intent-danger-border)] bg-[var(--intent-danger-bg)] px-1.5 py-px text-[11px] font-bold tabular-nums text-[var(--intent-danger-fg)]"
          >
            {t("activity.repeated", { count: occurrences })}
          </span>
        )}
        {item.detail && <span className="ml-1.5 text-[var(--text-subtle)]">{item.detail}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {item.audit_event_id !== undefined ? (
          <Link
            to={auditNavigationForEvent(orderId, item.audit_event_id).to}
            search={auditNavigationForEvent(orderId, item.audit_event_id).search}
            aria-label={t("activity.open_audit")}
            title={t("activity.open_audit")}
            className="text-[var(--text-subtle)] hover:text-[var(--text-primary)]"
          >
            ↗
          </Link>
        ) : item.stock_audit_id !== undefined ? (
          <Link
            to={auditLogNavigationFor(sku, item).to}
            search={auditLogNavigationFor(sku, item).search}
            aria-label={t("activity.open_audit")}
            title={t("activity.open_audit")}
            className="text-[var(--text-subtle)] hover:text-[var(--text-primary)]"
          >
            ↗
          </Link>
        ) : null}
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            aria-label={t("activity.delete_note")}
            title={t("activity.delete_note")}
            className="text-[var(--text-subtle)] hover:text-[var(--intent-danger-fg)]"
          >
            <Trash2 aria-hidden className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}
